import React, { useEffect, useState, useMemo } from "react";
import api from "../api";
import { Search, Save, Trash2, Plus, RefreshCw, AlertCircle } from "lucide-react";

const SEVERITY_COLOR = { critical: "#D93025", important: "#F9AB00", minor: "#5F6368" };
const CHAIN_LABEL = {
  1: "① Fondamenta tecniche",
  2: "② Google ti capisce",
  3: "③ Google si fida",
  4: "④ Le AI ti citano",
};
const SEVERITY_LABEL = { critical: "Critico", important: "Importante", minor: "Minore" };
const SOURCE_LABEL = {
  lighthouse: "Lighthouse", dataforseo_onpage: "DataForSEO On-Page",
  robots: "robots.txt", jsonld: "JSON-LD", manual: "Manuale",
};

/**
 * Admin CRUD for the problem_catalog collection. Every "check" the
 * analyzer can detect is a row here — admin can tune title, business
 * meaning, severity and chain-link without redeploying.
 */
export default function ProblemCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterChain, setFilterChain] = useState("all");
  const [editing, setEditing] = useState({}); // {id: draft} while typing
  const [saving, setSaving] = useState({});
  const [msg, setMsg] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState({
    key: "", severity: "important", chain_link: 1, source: "manual",
    title_it: "", meaning_for_business: "", enabled: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/problem-catalog");
      setRows(data.items || []);
    } catch (e) {
      setMsg({ ok: false, text: "Errore caricamento: " + (e.response?.data?.detail || e.message) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filterChain !== "all" && r.chain_link !== Number(filterChain)) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return r.key.toLowerCase().includes(s)
        || (r.title_it || "").toLowerCase().includes(s)
        || (r.meaning_for_business || "").toLowerCase().includes(s);
    });
  }, [rows, q, filterChain]);

  const startEdit = (r) => setEditing({ ...editing, [r.id]: { ...r } });
  const updateEdit = (id, patch) => setEditing({ ...editing, [id]: { ...editing[id], ...patch } });
  const cancelEdit = (id) => {
    const e = { ...editing }; delete e[id]; setEditing(e);
  };

  const save = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    setSaving({ ...saving, [id]: true });
    try {
      await api.patch(`/problem-catalog/${id}`, draft);
      setMsg({ ok: true, text: "Salvato." });
      cancelEdit(id);
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving({ ...saving, [id]: false }); }
  };

  const del = async (r) => {
    if (!window.confirm(`Eliminare il problema "${r.key}"? Non sarà più rilevato dalle analisi.`)) return;
    try {
      await api.delete(`/problem-catalog/${r.id}`);
      await load();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  const create = async () => {
    if (!newRow.key || !newRow.title_it) {
      setMsg({ ok: false, text: "Servono almeno key e title_it" });
      return;
    }
    try {
      await api.post("/problem-catalog", newRow);
      setNewRow({ key: "", severity: "important", chain_link: 1, source: "manual",
                    title_it: "", meaning_for_business: "", enabled: true });
      setCreating(false);
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="problem-catalog-page">
      <div className="flex items-center justify-between mb-4" style={{flexWrap:"wrap",gap:12}}>
        <div>
          <div className="h-display">Catalogo problemi</div>
          <div className="text-muted mt-2" style={{maxWidth:640}}>
            Dizionario dei problemi tecnici che il report può rilevare.
            Modifica titoli e spiegazioni orientate al business senza redeploy.
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setCreating(true)}
          data-testid="pc-add-btn"><Plus size={16}/> Aggiungi problema</button>
      </div>

      {msg && (
        <div className={`badge ${msg.ok ? "badge-green" : "badge-red"} mb-3`}
          data-testid="pc-message">{msg.text}</div>
      )}

      <div className="card mb-3" style={{padding:12}}>
        <div className="flex items-center gap-3" style={{flexWrap:"wrap"}}>
          <div style={{position:"relative", flex:"1 1 320px"}}>
            <Search size={14} style={{position:"absolute", left:10, top:12, color:"var(--mv-muted)"}}/>
            <input className="input" placeholder="Cerca per key, titolo o spiegazione…"
              style={{paddingLeft:32}}
              value={q} onChange={e=>setQ(e.target.value)}
              data-testid="pc-search"/>
          </div>
          <select className="input" style={{maxWidth:260}} value={filterChain}
            onChange={e=>setFilterChain(e.target.value)}
            data-testid="pc-filter-chain">
            <option value="all">Tutti gli anelli ({rows.length})</option>
            {[1,2,3,4].map(n => {
              const cnt = rows.filter(r=>r.chain_link===n).length;
              return <option key={n} value={n}>{CHAIN_LABEL[n]} ({cnt})</option>;
            })}
          </select>
          <button onClick={load} className="btn btn-ghost" data-testid="pc-refresh"
            title="Ricarica"><RefreshCw size={14}/></button>
        </div>
      </div>

      {creating && (
        <div className="card mb-3" style={{borderLeft:"4px solid var(--mv-primary)"}}>
          <div className="h-section mb-2">Nuovo problema</div>
          <div className="grid gap-2" style={{gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))"}}>
            <div>
              <label className="text-small text-muted">Key (unica, snake_case)</label>
              <input className="input" value={newRow.key}
                onChange={e=>setNewRow({...newRow, key: e.target.value})}
                data-testid="pc-new-key" placeholder="es. duplicate_title"/>
            </div>
            <div>
              <label className="text-small text-muted">Gravità</label>
              <select className="input" value={newRow.severity}
                onChange={e=>setNewRow({...newRow, severity: e.target.value})}
                data-testid="pc-new-severity">
                <option value="critical">Critico</option>
                <option value="important">Importante</option>
                <option value="minor">Minore</option>
              </select>
            </div>
            <div>
              <label className="text-small text-muted">Anello catena</label>
              <select className="input" value={newRow.chain_link}
                onChange={e=>setNewRow({...newRow, chain_link: Number(e.target.value)})}
                data-testid="pc-new-chain">
                {[1,2,3,4].map(n => <option key={n} value={n}>{CHAIN_LABEL[n]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-small text-muted">Fonte</label>
              <select className="input" value={newRow.source}
                onChange={e=>setNewRow({...newRow, source: e.target.value})}
                data-testid="pc-new-source">
                {Object.entries(SOURCE_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <label className="text-small text-muted mt-2" style={{display:"block"}}>Titolo (italiano semplice)</label>
          <input className="input" value={newRow.title_it}
            onChange={e=>setNewRow({...newRow, title_it: e.target.value})}
            data-testid="pc-new-title" placeholder="es. Più pagine hanno lo stesso titolo"/>
          <label className="text-small text-muted mt-2" style={{display:"block"}}>Cosa significa per il business</label>
          <textarea className="input" rows={2} value={newRow.meaning_for_business}
            onChange={e=>setNewRow({...newRow, meaning_for_business: e.target.value})}
            data-testid="pc-new-meaning"
            placeholder="es. Google non capisce quale pagina mostrare e spesso non ne mostra nessuna."/>
          <div className="flex gap-2 mt-3">
            <button className="btn btn-primary" onClick={create} data-testid="pc-new-save">
              <Save size={14}/> Crea
            </button>
            <button className="btn btn-ghost" onClick={()=>setCreating(false)}>Annulla</button>
          </div>
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="text-muted" data-testid="pc-loading">Caricamento…</div>
      )}

      <div className="grid gap-2">
        {filtered.map(r => {
          const draft = editing[r.id];
          const isEditing = !!draft;
          const shown = draft || r;
          return (
            <div key={r.id} className="card" data-testid={`pc-row-${r.key}`}
              style={{padding:14, opacity: r.enabled ? 1 : 0.55,
                borderLeft: `4px solid ${SEVERITY_COLOR[shown.severity]}`}}>
              <div className="flex items-start justify-between gap-3" style={{flexWrap:"wrap"}}>
                <div style={{flex:"1 1 400px"}}>
                  <div className="flex items-center gap-2 mb-1" style={{flexWrap:"wrap"}}>
                    <span className="badge" style={{background:SEVERITY_COLOR[shown.severity], color:"#fff"}}>
                      {SEVERITY_LABEL[shown.severity]}
                    </span>
                    <span className="badge badge-gray">{CHAIN_LABEL[shown.chain_link]}</span>
                    <span className="badge badge-gray">{SOURCE_LABEL[shown.source] || shown.source}</span>
                    <code className="text-small text-muted">{r.key}</code>
                    {!r.enabled && <span className="badge badge-red">Disattivato</span>}
                  </div>
                  {isEditing ? (
                    <>
                      <input className="input" value={draft.title_it}
                        onChange={e=>updateEdit(r.id, {title_it: e.target.value})}
                        placeholder="Titolo semplice"
                        data-testid={`pc-edit-title-${r.key}`}/>
                      <textarea className="input mt-2" rows={2}
                        value={draft.meaning_for_business}
                        onChange={e=>updateEdit(r.id, {meaning_for_business: e.target.value})}
                        placeholder="Cosa significa per il business"
                        data-testid={`pc-edit-meaning-${r.key}`}/>
                      <div className="flex gap-2 mt-2" style={{flexWrap:"wrap"}}>
                        <select className="input" style={{maxWidth:180}} value={draft.severity}
                          onChange={e=>updateEdit(r.id, {severity: e.target.value})}>
                          <option value="critical">Critico</option>
                          <option value="important">Importante</option>
                          <option value="minor">Minore</option>
                        </select>
                        <select className="input" style={{maxWidth:220}} value={draft.chain_link}
                          onChange={e=>updateEdit(r.id, {chain_link: Number(e.target.value)})}>
                          {[1,2,3,4].map(n => <option key={n} value={n}>{CHAIN_LABEL[n]}</option>)}
                        </select>
                        <label className="text-small flex items-center gap-1">
                          <input type="checkbox" checked={draft.enabled}
                            onChange={e=>updateEdit(r.id, {enabled: e.target.checked})}/>
                          Attivo
                        </label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{fontWeight:600, fontSize:15}}>{r.title_it}</div>
                      <div className="text-small text-muted mt-1">{r.meaning_for_business}</div>
                    </>
                  )}
                </div>
                <div className="flex gap-2 items-start">
                  {isEditing ? (
                    <>
                      <button className="btn btn-primary" disabled={saving[r.id]}
                        onClick={()=>save(r.id)} data-testid={`pc-save-${r.key}`}>
                        <Save size={14}/> {saving[r.id] ? "…" : "Salva"}
                      </button>
                      <button className="btn btn-ghost" onClick={()=>cancelEdit(r.id)}>Annulla</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-ghost" onClick={()=>startEdit(r)}
                        data-testid={`pc-edit-${r.key}`}>Modifica</button>
                      <button className="btn btn-ghost" onClick={()=>del(r)}
                        title="Elimina" style={{color:"var(--mv-danger)"}}
                        data-testid={`pc-del-${r.key}`}><Trash2 size={14}/></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && !loading && (
          <div className="text-muted card" style={{padding:24, textAlign:"center"}}>
            <AlertCircle size={20} style={{marginBottom:8}}/>
            <div>Nessun problema corrisponde ai filtri.</div>
          </div>
        )}
      </div>
    </div>
  );
}
